import { Injectable, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from './auth.service';

export type ReadingActivityKind = 'book' | 'magazine';

export interface ReadingActivityItem {
  kind: ReadingActivityKind;
  id: number;
  title: string;
  subtitle: string;
  imageUrl: string;
  progress: number;
  openedAt: number;
}

const STORAGE_PREFIX = 'at_reading_activity_v1_';
const MAX_ITEMS = 12;

@Injectable({ providedIn: 'root' })
export class ReadingActivityService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly auth = inject(AuthService);
  private readonly items = signal<ReadingActivityItem[]>([]);

  readonly recent = this.items.asReadonly();
  readonly latest = computed(() => this.items()[0] ?? null);

  constructor() {
    effect(() => {
      const userId = this.auth.currentUser()?.UsrId ?? 0;
      this.items.set(this.readForUser(userId));
    });
  }

  trackBook(input: {
    id: number;
    title: string;
    author?: string | null;
    imageUrl?: string | null;
  }): void {
    this.upsert({
      kind: 'book',
      id: Number(input.id),
      title: (input.title || 'Untitled book').trim(),
      subtitle: (input.author || 'Unknown Author').trim(),
      imageUrl: (input.imageUrl || '').trim(),
      progress: 0,
      openedAt: Date.now()
    });
  }

  trackMagazine(input: {
    id: number;
    title: string;
    issue?: string | null;
    imageUrl?: string | null;
  }): void {
    this.upsert({
      kind: 'magazine',
      id: Number(input.id),
      title: (input.title || 'Untitled magazine').trim(),
      subtitle: (input.issue || 'Magazine').trim(),
      imageUrl: (input.imageUrl || '').trim(),
      progress: 0,
      openedAt: Date.now()
    });
  }

  private upsert(incoming: ReadingActivityItem): void {
    const userId = this.auth.currentUser()?.UsrId ?? 0;
    if (!userId || !Number.isFinite(incoming.id) || incoming.id <= 0) return;

    const existing = this.readForUser(userId);
    const prev = existing.find((x) => x.kind === incoming.kind && x.id === incoming.id);
    const progress = this.nextProgress(prev?.progress ?? 0);

    const nextItem: ReadingActivityItem = {
      ...incoming,
      progress,
      imageUrl: incoming.imageUrl || prev?.imageUrl || '',
      subtitle: incoming.subtitle || prev?.subtitle || ''
    };

    const next = [nextItem, ...existing.filter((x) => !(x.kind === incoming.kind && x.id === incoming.id))].slice(
      0,
      MAX_ITEMS
    );
    this.items.set(next);
    this.writeForUser(userId, next);
  }

  private nextProgress(current: number): number {
    if (current <= 0) return 12;
    if (current >= 95) return 95;
    return Math.min(95, current + 18);
  }

  private storageKey(userId: number): string {
    return `${STORAGE_PREFIX}${userId}`;
  }

  private readForUser(userId: number): ReadingActivityItem[] {
    if (!userId || !isPlatformBrowser(this.platformId)) return [];
    try {
      const raw = localStorage.getItem(this.storageKey(userId));
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((row) => this.normalize(row))
        .filter((row): row is ReadingActivityItem => !!row)
        .sort((a, b) => b.openedAt - a.openedAt)
        .slice(0, MAX_ITEMS);
    } catch {
      return [];
    }
  }

  private writeForUser(userId: number, list: ReadingActivityItem[]): void {
    if (!userId || !isPlatformBrowser(this.platformId)) return;
    localStorage.setItem(this.storageKey(userId), JSON.stringify(list));
  }

  private normalize(row: any): ReadingActivityItem | null {
    const kind = row?.kind === 'magazine' ? 'magazine' : row?.kind === 'book' ? 'book' : null;
    const id = Number(row?.id);
    if (!kind || !Number.isFinite(id) || id <= 0) return null;
    return {
      kind,
      id,
      title: String(row?.title || '').trim() || (kind === 'book' ? 'Untitled book' : 'Untitled magazine'),
      subtitle: String(row?.subtitle || '').trim(),
      imageUrl: String(row?.imageUrl || '').trim(),
      progress: Math.max(0, Math.min(100, Number(row?.progress) || 0)),
      openedAt: Number(row?.openedAt) || Date.now()
    };
  }
}
